/**
 * P0-PM-001: Installation Progress Indicator
 *
 * Displays active installations in the navigation bar
 * with click-to-view-details functionality.
 */

import { Loader2, Package, CheckCircle, XCircle } from 'lucide-react';
import { useInstallationProgress } from '@/hooks/useInstallationProgress';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface InstallationIndicatorProps {
  onViewDetails?: (instanceId: string) => void;
}

export function InstallationIndicator({ onViewDetails }: InstallationIndicatorProps) {
  const {
    activeInstallations,
    completedInstallations,
    failedInstallations,
    hasActiveInstallations,
  } = useInstallationProgress();

  // Don't show indicator if no installations
  const totalInstallations = activeInstallations.length +
    completedInstallations.length +
    failedInstallations.length;

  if (totalInstallations === 0) {
    return null;
  }

  // Calculate overall progress
  const totalProgress = activeInstallations.length > 0
    ? activeInstallations.reduce((sum, inst) => sum + inst.progress, 0) /
      activeInstallations.length
    : 100;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          aria-label={`${activeInstallations.length} installations in progress`}
        >
          {hasActiveInstallations ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-blue-400" aria-hidden="true" />
              <span className="ml-2 text-sm font-medium">
                {activeInstallations.length} installing
              </span>
              {/* Progress ring */}
              <svg
                className="absolute -right-1 -top-1 h-6 w-6 -rotate-90"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  className="text-slate-700"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={`${totalProgress * 0.628} 62.8`}
                  className="text-blue-400"
                />
              </svg>
            </>
          ) : (
            <>
              <Package className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <span className="ml-2 text-sm text-slate-400">
                {completedInstallations.length} complete
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Installation Progress</h3>
            <span className="text-xs text-slate-400">
              {activeInstallations.length + completedInstallations.length + failedInstallations.length} total
            </span>
          </div>

          {/* Active installations */}
          {activeInstallations.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-300">
                In Progress ({activeInstallations.length})
              </p>
              {activeInstallations.map((inst) => (
                <div
                  key={`${inst.instanceId}-${inst.bobName}`}
                  className="rounded-lg border border-slate-700 bg-slate-800/30 p-3"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{inst.bobName}</p>
                      <p className="text-xs text-slate-400 capitalize">
                        {inst.state.replace('_', ' ')}
                      </p>
                    </div>
                    <Loader2 className="h-4 w-4 text-blue-400 animate-spin flex-shrink-0" aria-hidden="true" />
                  </div>

                  {/* Progress bar */}
                  <div className="space-y-1">
                    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${inst.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 text-right">
                      {inst.progress}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Completed installations */}
          {completedInstallations.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-emerald-400">
                Completed ({completedInstallations.length})
              </p>
              {completedInstallations.slice(0, 3).map((inst) => (
                <div
                  key={`${inst.instanceId}-${inst.bobName}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="truncate flex-1">{inst.bobName}</span>
                  <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0 ml-2" aria-hidden="true" />
                </div>
              ))}
              {completedInstallations.length > 3 && (
                <p className="text-xs text-slate-400">
                  +{completedInstallations.length - 3} more
                </p>
              )}
            </div>
          )}

          {/* Failed installations */}
          {failedInstallations.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-red-400">
                Failed ({failedInstallations.length})
              </p>
              {failedInstallations.slice(0, 2).map((inst) => (
                <div
                  key={`${inst.instanceId}-${inst.bobName}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="truncate flex-1">{inst.bobName}</span>
                  <XCircle className="h-4 w-4 text-red-400 flex-shrink-0 ml-2" aria-hidden="true" />
                </div>
              ))}
            </div>
          )}

          {/* View details button */}
          {onViewDetails && activeInstallations.length > 0 && (
            <Button
              size="sm"
              className="w-full"
              onClick={() => {
                const firstInstance = activeInstallations[0]?.instanceId;
                if (firstInstance) {
                  onViewDetails(firstInstance);
                }
              }}
            >
              View Details
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
